import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useSuspenseQuery } from '@apollo/client';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { DeviceAlertsModalTab } from '@features/deviceAlertsModal/types';
import {
  GET_AFTER_UPDATE_ALERT_TASKS,
  GET_AFTER_UPDATE_DGA_TASKS,
} from '@features/deviceConfigurationModal/graphql/queries/getAfterUpdateTasksFields';
import { DeviceDGAModalTab } from '@features/dga/types/modalTabs';
import { Plural, Trans } from '@lingui/macro';
import { CheckCircle } from '@mui/icons-material';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { Suspense, type FC } from 'react';

interface Props {
  deviceId: string;
  onClose: () => void;
}

export const AfterUpdateTasksModal = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('afterUpdateTasksModal');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'sm' }}>
      {state.open && <Component deviceId={state.deviceId} onClose={onClose} />}
    </GlobalModal>
  );
};

const Component: FC<Props> = ({ deviceId, onClose }) => {
  return (
    <>
      <ModalTitle
        title={
          <Box display="flex" gap={2} alignItems="center">
            <CheckCircle color="success" />
            <Trans>Dispositivo reactivado</Trans>
          </Box>
        }
      />
      <ModalContent>
        <Typography>
          <Trans>
            El dispositivo ha sido actualizado de mantenimiento a activo. Se recomienda revisar las
            siguientes configuraciones:
          </Trans>
        </Typography>
        <Typography fontWeight={600} mt={2} fontSize={15}>
          <Trans>Configuraciones recomendadas:</Trans>
        </Typography>

        <Box mt={2} display="flex" flexDirection="column" gap={2}>
          <ApolloErrorBoundary variant="modal">
            <Suspense fallback={<Skeleton />}>
              <DGATasks deviceId={deviceId} />
            </Suspense>
          </ApolloErrorBoundary>

          <ApolloErrorBoundary variant="modal">
            <Suspense fallback={<Skeleton />}>
              <AlertTasks deviceId={deviceId} />
            </Suspense>
          </ApolloErrorBoundary>
        </Box>
      </ModalContent>
      <ModalActions onClose={onClose} />
    </>
  );
};

interface TaskViewProps {
  title: JSX.Element;
  description: JSX.Element;
  status: 'active' | 'inactive';
  buttonAction: () => void;
  activeDescription: JSX.Element;
  inactiveDescription: JSX.Element;
}

const TaskView: FC<TaskViewProps> = ({
  title,
  status,
  buttonAction,
  description,
  activeDescription,
  inactiveDescription,
}) => {
  const statusLabel = status === 'active' ? <Trans>Activo</Trans> : <Trans>Inactivo</Trans>;
  const statusColor = status === 'active' ? green[500] : red[500];
  const statusDescription = status === 'active' ? activeDescription : inactiveDescription;
  return (
    <Box border={1} borderColor="divider" borderRadius={2} py={1} px={1.5}>
      <Box display="grid" gridTemplateColumns="1fr auto" gap={2}>
        <Box>
          <Typography fontWeight="bold" fontSize={16}>
            {title}
          </Typography>
          <Typography fontSize={15} color="text.secondary" my={0.5}>
            <Trans>{description}</Trans>
          </Typography>
        </Box>
        <Box>
          <Button onClick={buttonAction} size="small" variant="contained" color="info">
            <Trans>Configurar</Trans>
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={2} mt={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box bgcolor={statusColor} height={14} width={14} borderRadius={16} />
          <Typography fontWeight={500} whiteSpace="nowrap">
            Estado: {statusLabel}
          </Typography>
        </Box>
        <Typography fontSize={15} color="text.secondary">
          {statusDescription}
        </Typography>
      </Box>
    </Box>
  );
};

const DGATasks: FC<{ deviceId: string }> = ({ deviceId }) => {
  const { data } = useSuspenseQuery(GET_AFTER_UPDATE_DGA_TASKS, {
    variables: {
      deviceId,
    },
  });
  const { setModalState } = useGlobalModal('updateDGAConfigurationModal');

  const openModal = () => {
    setModalState({
      open: true,
      deviceId,
      selectedTab: DeviceDGAModalTab.Submissions,
    });
  };

  const status = data.device.dga?.configuration.enabled ? 'active' : 'inactive';

  return (
    <TaskView
      buttonAction={openModal}
      description={<Trans>Actualizar los envíos periódicos a la DGA</Trans>}
      status={status}
      title={<Trans>Configurar envíos DGA</Trans>}
      activeDescription={<Trans>Se están enviando datos a la DGA</Trans>}
      inactiveDescription={<Trans>Los envíos están desactivados</Trans>}
    />
  );
};

const AlertTasks: FC<{ deviceId: string }> = ({ deviceId }) => {
  const { data } = useSuspenseQuery(GET_AFTER_UPDATE_ALERT_TASKS, {
    variables: {
      deviceId,
    },
  });

  const { setModalState } = useGlobalModal('deviceAlerts');

  const openModal = () => {
    setModalState({
      open: true,
      deviceId,
      selectedTab: DeviceAlertsModalTab.BasicConfiguration,
    });
  };

  const alertsCount = data.device.alerts.activeConfigurations.length;

  const status = alertsCount > 0 ? 'active' : 'inactive';

  return (
    <TaskView
      buttonAction={openModal}
      description={<Trans>Actualizar configuración de alertas</Trans>}
      status={status}
      title={<Trans>Configurar alertas</Trans>}
      activeDescription={
        <Plural
          value={alertsCount}
          one="Hay # configuración activa"
          other="Hay # configuraciones activas"
        />
      }
      inactiveDescription={<Trans>No hay configuraciones activas</Trans>}
    />
  );
};
